import React from 'react';
import { Card, Space, Tag, Typography } from 'antd';
import { Link } from 'react-router-dom';
import PaginatedAdminList, { ListItem } from '../../../components/containers/PaginatedAdminList';
import { queries } from '../../../graphql/graphql';
import { IPhase } from '../../../modules/project';

const { Text } = Typography;

interface ProjectListItem extends ListItem {
  name?: string;
  status?: string;
  ownerId?: string;
  owner?: {
    id?: string;
    name?: string;
    email?: string;
  };
  createdAt?: string;
  users?: { id?: string; name?: string; email?: string }[];
  phases?: IPhase[];
}

const formatDate = (value?: string | number) => {
  if (value === null || value === undefined || value === '') return '—';
  const date =
    typeof value === 'number'
      ? new Date(value)
      : (() => {
          const trimmed = value.trim();
          const numeric = Number(trimmed);
          if (!Number.isNaN(numeric) && trimmed !== '') {
            return new Date(numeric);
          }
          return new Date(value);
        })();
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

const getStatusColor = (status?: string) => {
  if (!status) return 'default';
  if (status.toLowerCase() === 'active') return 'green';
  if (status.toLowerCase() === 'draft') return 'gold';
  return 'blue';
};

const ProjectCard: React.FC<{ item: ListItem }> = ({ item }) => {
  const project = item as ProjectListItem;
  const usersCount = project.users?.length || 0;
  const phases = Array.isArray(project.phases) ? project.phases : [];
  const phasesCount = phases.length;
  const completedPhases = phases.filter(phase => phase?.status === 'completed').length;
  const activePhase = phases.find(phase => phase?.isActive) || null;
  const activePhaseSequence =
    typeof activePhase?.sequence === 'number' ? activePhase.sequence : null;
  const activePhaseProgress =
    typeof activePhase?.progress === 'number' ? Math.round(activePhase.progress) : 0;
  const ownerLabel = project.owner?.name || project.owner?.email || project.ownerId || '—';

  return (
    <Card
      hoverable
      actions={[
        <Link key={`view-${project.id}`} to={`/admin/project/${project.id}`}>
          View
        </Link>,
      ]}>
      <Card.Meta
        title={project.name || `Project ${project.id}`}
        description={
          <Space direction="vertical" size={6}>
            {project.status && (
              <Tag color={getStatusColor(project.status)}>{project.status.toUpperCase()}</Tag>
            )}
            <Space size={8}>
              <Tag color="default">
                Phases: {completedPhases}/{phasesCount}
              </Tag>
              <Tag color="default">
                Active: {activePhaseSequence !== null ? activePhaseSequence : '—'}
              </Tag>
              {/* <Tag color="default">Progress: {activePhaseProgress}%</Tag> */}
            </Space>
            <Text type="secondary">
              Owner:{' '}
              {project.owner?.id ? (
                <Link to={`/admin/user/${project.owner.id}`}>{ownerLabel}</Link>
              ) : (
                ownerLabel
              )}
            </Text>
            <Text type="secondary">Created: {formatDate(project.createdAt)}</Text>
          </Space>
        }
      />
    </Card>
  );
};

const ProjectCardWrapper: React.FC<{
  item: ListItem;
  refetch: () => void;
  prefix: string;
  roles?: string[];
}> = ({ item }) => <ProjectCard item={item} />;

const ListProjects: React.FC = () => {
  return (
    <PaginatedAdminList
      prefix="project"
      query={queries.project.PROJECTS}
      inline={false}
      editable={false}
      canCreate={false}
      adminView={true}
      CustomCard={ProjectCardWrapper}
      defaultPageSize={12}
    />
  );
};

export default ListProjects;
